import type { Action, Agent, Hook, Validation } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { CREATE_VALIDATION_TASK_FUNCTION_ID, HOOK_EVALUATE_PREDICTION } from '@/constants';
import { toAgentId, useCreateAction, useCreateHook, useCreateValidation, useUpdateAgent } from '@/hooks/api';

export type UseAddValidationOnSuccess = ({
  validation,
  action,
  hook,
  agent,
}: {
  validation: Validation;
  action: Action;
  hook: Hook;
  agent: Agent;
}) => void;

export type UseAddValidationOnError = ({ error }: { error: Error }) => void;

export type UseAddValidationOpts = {
  onSuccess?: UseAddValidationOnSuccess;
  onError?: UseAddValidationOnError;
};

export const useAddValidation = (opts?: UseAddValidationOpts) => {
  const { onSuccess: onSuccessFnBase, onError: onErrorFnBase } = opts ?? {};

  const {
    mutateAsync: createValidation,
    isPending: isPendingCreateValidation,
    isSuccess: isSuccessCreateValidation,
    error: errorCreateValidation,
  } = useCreateValidation();

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const {
    mutateAsync: createHook,
    isPending: isPendingCreateHook,
    isSuccess: isSuccessCreateHook,
    error: errorCreateHook,
  } = useCreateHook();

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const addValidation = useCallback(
    async ({
      agentId,
      modelId,
      onSuccess: onSuccessFn,
      onError: onErrorFn,
    }: {
      agentId: string;
      modelId: string;
      onSuccess?: UseAddValidationOnSuccess;
      onError?: UseAddValidationOnError;
    }) => {
      const onSuccess: UseAddValidationOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseAddValidationOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const validation = await createValidation({ config: {} });

        const action = await createAction({
          config: { validationId: validation.validationId },
          functionId: CREATE_VALIDATION_TASK_FUNCTION_ID,
        });

        const hook = await createHook({
          functionId: HOOK_EVALUATE_PREDICTION,
          config: { modelId },
          trigger: 'Prediction is Created',
          falseActionId: action.actionId,
        });

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, validation.validationId, action.actionId, hook.hookId],
          }),
        });

        onSuccess?.({ validation, action, hook, agent });
        return validation;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        }
      }
    },
    [createAction, createHook, createValidation, onErrorFnBase, onSuccessFnBase, updateAgent]
  );

  return {
    addValidation,
    isPending: isPendingCreateValidation || isPendingCreateAction || isPendingCreateHook || isPendingUpdateAgent,
    isSuccess: isSuccessCreateValidation && isSuccessCreateAction && isSuccessCreateHook && isSuccessUpdateAgent,
    errors: [errorCreateValidation, errorCreateAction, errorCreateHook, errorUpdateAgent].filter((e) => !!e),
  };
};
